'use client';

import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { explorerAnalytics } from '@/lib/analytics';
import { copyToClipboard } from '@/lib/copyToClipboard';
import { useEventExplorerStore } from '@/stores/useEventExplorerStore';
import { Button } from '@components/components/ui/button';
import { Check, Copy } from 'lucide-react';
import { createHighlighter } from 'shikiv2';

interface EventJsonViewerProps {
  content: object;
}

export const EventJsonViewer: React.FC<EventJsonViewerProps> = ({
  content,
}) => {
  const codeContainerRef = useRef<HTMLDivElement>(null);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [copied, setCopied] = useState(false);
  const { piiRedactionEnabled } = useEventExplorerStore();

  const jsonString = JSON.stringify(
    piiRedactionEnabled ? redactPii(content) : content,
    null,
    2
  );

  useEffect(() => {
    let cancelled = false;
    setIsHighlighted(false);

    async function highlight() {
      try {
        const highlighter = await createHighlighter({
          themes: ['light-plus'],
          langs: ['json'],
        });
        const html = highlighter.codeToHtml(jsonString, {
          lang: 'json',
          theme: 'light-plus',
        });
        if (!cancelled && codeContainerRef.current) {
          codeContainerRef.current.innerHTML = html;
          setIsHighlighted(true);
        }
      } catch {
        if (!cancelled) {
          setIsHighlighted(false);
        }
      }
    }

    highlight();
    return () => {
      cancelled = true;
    };
  }, [jsonString]);

  const handleCopy = async () => {
    try {
      await copyToClipboard(jsonString);
      setCopied(true);
      explorerAnalytics.eventPayloadCopied({
        payloadSize: jsonString?.length || 0,
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard not available
    }
  };

  return (
    <div className="relative h-full">
      {/* Toolbar */}
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white px-4 py-2">
        <span className="text-xs text-gray-400">
          JSON
          {piiRedactionEnabled && (
            <span className="ml-2 text-amber-500">(PII redacted)</span>
          )}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleCopy}
            className="h-7 w-7 text-gray-400 hover:text-gray-600"
            title="Copy JSON"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-green-500" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </div>

      {/* JSON content */}
      <div className="p-4" data-pendo-mask>
        <div ref={codeContainerRef} />
        {!isHighlighted && (
          <pre className="whitespace-pre-wrap text-sm font-mono text-gray-700">
            {jsonString}
          </pre>
        )}
      </div>
    </div>
  );
};

// ─── PII Redaction ─────────────────────────────────────────────────────────

const PII_JSON_KEYS = new Set([
  'first_name',
  'last_name',
  'email',
  'phone',
  'company',
  'billing_addr1',
  'billing_addr2',
  'shipping_address',
  'line1',
  'line2',
  'line3',
  'city',
  'state',
  'zip',
  'country',
  'ip',
  'ip_address',
  'card_last4',
  'masked_number',
]);

const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

function redactPii(obj: unknown): unknown {
  if (typeof obj === 'string') {
    return obj.replace(EMAIL_REGEX, '***@***.***');
  }
  if (Array.isArray(obj)) {
    return obj.map(redactPii);
  }
  if (obj && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>).map(([key, value]) => {
        if (PII_JSON_KEYS.has(key) && typeof value === 'string') {
          return [key, '***REDACTED***'];
        }
        return [key, redactPii(value)];
      })
    );
  }
  return obj;
}
